import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { tokens } from '../../global/theme/tokens';
import useFetch from '../../api/useFetch';
import ExoAvatar from '../../components/exo/ExoAvatar';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import { CapacityHeaderStat } from '../../components/exo/project/capacity/CapacityProjectCard';
import EmployeeCapacityProject from './EmployeeCapacityProject';
import {
  buildEmployeeProjectsPath,
  employeeHeaderStats,
  employeeName,
} from './capacityFilter';

/**
 * One employee of the capacity list. Collapsed it is a single row, expanded it
 * shows every task of the employee at a glance, grouped by project. Projects
 * without a task to show stay out of the way.
 */
const EmployeeCapacityCard = ({
  user,
  // capacity entry of the employee, counters included
  entry,
  // projects of this employee, cached by the list once they were loaded
  projects,
  isOpen,
  onToggle,
  onProjectsLoaded,
  // the projects the filter bar is narrowed to
  projectIds = [],
  // finished tasks stay hidden unless they are asked for
  hideDone,
  processingTaskIds = [],
  // the task callbacks take the employee and the project the task belongs to
  onTaskToggle,
  onTaskEdit,
  onTaskDelete,
  onTaskUsersChange,
  onTaskFilesChange,
  onTaskLinkAdd,
  onTaskLinkDelete,
  onTaskCommentSubmit,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const path = useMemo(
    () => buildEmployeeProjectsPath(user.id, projectIds),
    [user.id, projectIds],
  );

  // null path = no request: the projects are loaded on the first expand and the
  // list keeps them, so collapsing and reopening costs nothing
  const { apiData } = useFetch(isOpen && !projects ? path : null);

  useEffect(() => {
    if (apiData) onProjectsLoaded(user.id, apiData.data || []);
  }, [apiData]);

  const name = employeeName(user);

  // only projects with something to show - the rest is noise on a screen
  // that is about what an employee is working on
  const visibleProjects = useMemo(
    () =>
      (projects || [])
        .map((project) => ({
          project,
          tasks: hideDone
            ? (project.tasks || []).filter((task) => !task.status)
            : project.tasks || [],
        }))
        .filter(({ tasks }) => tasks.length),
    [projects, hideDone],
  );

  return (
    // shrink-0: inside the scrolling list a card keeps its own height
    <Box
      className="rounded-xl overflow-hidden backdrop-blur-lg shrink-0"
      sx={{ backgroundColor: colors.glass }}
    >
      <Box
        onClick={onToggle}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-2 cursor-pointer"
        sx={{ ':hover': { backgroundColor: `${colors.text}08` } }}
      >
        {/* the row toggles too, so the click must not reach it twice */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="shrink-0"
        >
          <KeyboardArrowRightIcon
            sx={{
              fontSize: '20px',
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>

        <Box className="shrink-0 flex">
          <ExoAvatar
            name={name}
            picture={user.picture?.thumbnailUrl}
            size="30px"
          />
        </Box>

        {/* flex-basis 0: the row wraps, so a long name is cut off instead of
            pushing the counter onto the next line */}
        <Box
          className="flex flex-col"
          sx={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}
        >
          <Typography
            fontWeight={600}
            noWrap
            title={name}
            sx={{ fontSize: { xs: '14px', md: '15px' } }}
          >
            {name}
          </Typography>
          {Boolean(user.position) && (
            <Typography fontSize="10px" className="opacity-60" noWrap>
              {user.position}
            </Typography>
          )}
        </Box>

        {/* the counters come with the capacities, so they are there before the
            employee is opened */}
        <Box className="flex flex-wrap items-center justify-between sm:justify-end gap-x-4 gap-y-1 w-full sm:w-auto shrink-0 pr-1 order-last sm:order-none">
          {employeeHeaderStats(entry, t).map((stat) => (
            <CapacityHeaderStat
              key={stat.key}
              label={stat.label}
              value={stat.value}
            />
          ))}
        </Box>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        {/* the nested task cards need every pixel on a phone */}
        <Box className="flex flex-col gap-3 px-1 sm:px-3 pb-3">
          {!projects ? (
            <ExoSkeleton n={2} size="70px" />
          ) : visibleProjects.length ? (
            visibleProjects.map(({ project, tasks }) => (
              <EmployeeCapacityProject
                key={project.id}
                project={project}
                tasks={tasks}
                processingTaskIds={processingTaskIds}
                onTaskToggle={(task) => onTaskToggle(user.id, project.id, task)}
                onTaskEdit={(task) => onTaskEdit(user.id, project.id, task)}
                onTaskDelete={(task) => onTaskDelete(user.id, project.id, task)}
                onTaskUsersChange={() => onTaskUsersChange(user.id)}
                onTaskFilesChange={(task, files) =>
                  onTaskFilesChange(user.id, project.id, task, files)
                }
                onTaskLinkAdd={(task, link) =>
                  onTaskLinkAdd(user.id, project.id, task, link)
                }
                onTaskLinkDelete={(task, resource) =>
                  onTaskLinkDelete(user.id, project.id, task, resource)
                }
                onTaskCommentSubmit={(task, text) =>
                  onTaskCommentSubmit(user.id, project.id, task, text)
                }
              />
            ))
          ) : (
            <Typography fontSize="13px" className="opacity-50 py-2 px-2">
              {t('No open tasks')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default EmployeeCapacityCard;
